import * as pc from 'playcanvas'

const parseJsonValue = value => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const cameraDataItems = value => {
  let payload = parseJsonValue(value)
  for (let depth = 0; depth < 4 && payload && !Array.isArray(payload); depth += 1) {
    const nested = payload.data ?? payload.cameraData ?? payload.camera_data ?? payload.frames ?? payload.cameras
    if (nested === undefined || nested === payload) break
    payload = parseJsonValue(nested)
  }
  if (Array.isArray(payload)) return payload.filter(Boolean)
  return payload && typeof payload === 'object' ? [payload] : []
}

const vectorFrom = value => {
  value = parseJsonValue(value)
  if (!value) return null
  const x = Number(value.x ?? value.X ?? value[0])
  const y = Number(value.y ?? value.Y ?? value[1])
  const z = Number(value.z ?? value.Z ?? value[2])
  return [x, y, z].every(Number.isFinite) ? new pc.Vec3(x, y, z) : null
}

const firstVector = candidates => {
  for (const candidate of candidates) {
    const vector = vectorFrom(candidate)
    if (vector) return vector
  }
  return null
}

const matrixPosition = value => {
  value = parseJsonValue(value)
  const matrix = value?.data || value
  if (!matrix || typeof matrix.length !== 'number' || matrix.length < 16) return null
  return vectorFrom([matrix[12], matrix[13], matrix[14]])
}

const rotationMatrixArray = value => {
  value = parseJsonValue(value)
  if (!value) return null

  if (Array.isArray(value) || ArrayBuffer.isView(value)) {
    const rows = Array.from(value)
    if (rows.length >= 16 && !Array.isArray(rows[0]) && !ArrayBuffer.isView(rows[0])) {
      const matrixNumbers = rows.slice(0, 16).map(Number)
      const numbers = [matrixNumbers[0], matrixNumbers[1], matrixNumbers[2], matrixNumbers[4], matrixNumbers[5], matrixNumbers[6], matrixNumbers[8], matrixNumbers[9], matrixNumbers[10]]
      return numbers.every(Number.isFinite) ? numbers : null
    }
    if (rows.length >= 3 && rows.slice(0, 3).every(row => (
      (Array.isArray(row) || ArrayBuffer.isView(row)) && row.length >= 3
    ))) {
      const numbers = [
        rows[0][0], rows[0][1], rows[0][2],
        rows[1][0], rows[1][1], rows[1][2],
        rows[2][0], rows[2][1], rows[2][2]
      ].map(Number)
      return numbers.every(Number.isFinite) ? numbers : null
    }
    if (rows.length >= 9) {
      const numbers = rows.slice(0, 9).map(Number)
      return numbers.every(Number.isFinite) ? numbers : null
    }
  }

  if (typeof value === 'object') {
    const nested = value.elements ?? value.data ?? value.values ?? value.matrix
    if (nested && nested !== value) {
      const numbers = rotationMatrixArray(nested)
      if (numbers) return numbers
    }
    const keySets = [
      ['m00', 'm01', 'm02', 'm10', 'm11', 'm12', 'm20', 'm21', 'm22'],
      ['r00', 'r01', 'r02', 'r10', 'r11', 'r12', 'r20', 'r21', 'r22']
    ]
    for (const keys of keySets) {
      const numbers = keys.map(key => Number(value[key]))
      if (numbers.every(Number.isFinite)) return numbers
    }
  }
  return null
}

const itemRotationMatrix = item => {
  const candidates = [
    item?.rotation,
    item?.Rotation,
    item?.R,
    item?.rotationMatrix,
    item?.rotation_matrix,
    item?.cameraRotation,
    item?.camera_rotation,
    item?.camera?.rotation,
    item?.camera?.Rotation,
    item?.camera?.R,
    item?.camera?.rotationMatrix,
    item?.camera?.rotation_matrix,
    item?.transform,
    item?.matrix,
    item?.camera?.transform,
    item?.camera?.matrix,
    item?.pose?.rotation,
    item?.pose?.Rotation,
    item?.pose?.R,
    item?.pose?.rotationMatrix,
    item?.pose?.rotation_matrix
  ]
  for (const candidate of candidates) {
    const matrix = rotationMatrixArray(candidate)
    if (matrix) return matrix
  }
  return null
}

const quaternionArray = value => {
  value = parseJsonValue(value)
  if (!value) return null
  const x = Number(value.x ?? value.qx ?? value[0])
  const y = Number(value.y ?? value.qy ?? value[1])
  const z = Number(value.z ?? value.qz ?? value[2])
  const w = Number(value.w ?? value.qw ?? value[3])
  if (![x, y, z, w].every(Number.isFinite)) return null
  const length = Math.hypot(x, y, z, w)
  return length > 1e-8 ? [x / length, y / length, z / length, w / length] : null
}

const itemQuaternion = item => {
  const candidates = [
    item?.qvec,
    item?.Qvec,
    item?.qVec,
    item?.QVec,
    item?.camera?.qvec,
    item?.camera?.Qvec,
    item?.pose?.qvec,
    item?.pose?.Qvec,
    item?.quaternion,
    item?.Quaternion,
    item?.camera?.quaternion,
    item?.camera?.Quaternion,
    item?.pose?.quaternion,
    item?.pose?.Quaternion
  ]
  for (const candidate of candidates) {
    const quaternion = quaternionArray(candidate)
    if (quaternion) return quaternion
  }
  return null
}

const itemPosition = item => firstVector([
  item?.Position,
  item?.CameraPosition,
  item?.camera?.Position,
  item?.pose?.Position,
  item?.cameraPosition,
  item?.camera_position,
  item?.position,
  item?.translation,
  item?.origin,
  item?.t,
  item?.camera?.position,
  item?.pose?.position
]) || matrixPosition(item?.transform ?? item?.matrix ?? item?.camera_matrix ?? item?.cameraMatrix)

const itemExplicitFocus = item => firstVector([
  item?.focus,
  item?.focusPoint,
  item?.target,
  item?.lookAt,
  item?.look_at,
  item?.camera?.focus,
  item?.pose?.focus
])

const itemLookDirection = item => {
  const explicit = firstVector([
    item?.direction,
    item?.lookDirection,
    item?.look_direction,
    item?.forward,
    item?.camera?.direction,
    item?.pose?.direction
  ])
  if (explicit?.length() > 1e-6) return explicit.normalize()

  const matrix = itemRotationMatrix(item)
  if (matrix) {
    const direction = new pc.Vec3(matrix[2], matrix[5], matrix[8])
    if (direction.length() > 1e-6) return direction.normalize()
  }

  const quaternion = itemQuaternion(item)
  if (!quaternion) return null
  const [qx, qy, qz, qw] = quaternion
  const direction = new pc.Vec3(
    2 * qx * qz + 2 * qy * qw,
    2 * qy * qz - 2 * qx * qw,
    1 - 2 * qx * qx - 2 * qy * qy
  )
  return direction.length() > 1e-6 ? direction.normalize() : null
}

const cameraPathDiagonal = positions => {
  if (!positions.length) return 1
  const min = positions[0].clone()
  const max = positions[0].clone()
  for (const position of positions.slice(1)) {
    min.x = Math.min(min.x, position.x)
    min.y = Math.min(min.y, position.y)
    min.z = Math.min(min.z, position.z)
    max.x = Math.max(max.x, position.x)
    max.y = Math.max(max.y, position.y)
    max.z = Math.max(max.z, position.z)
  }
  return Math.max(max.sub(min).length(), 1)
}

const focusIsInFrontOfSamples = (focus, samples) => {
  const directionalSamples = samples.filter(sample => sample.direction && sample.direction.length() > 1e-6)
  if (!directionalSamples.length) return true
  return directionalSamples.every(sample => {
    const direction = sample.direction.clone().normalize()
    const toFocus = focus.clone().sub(sample.position)
    return toFocus.length() > 1e-6 && direction.dot(toFocus) > 1e-6
  })
}

const rayBoxIntersection = (origin, direction, bounds) => {
  const center = vectorFrom(bounds?.center)
  const halfExtents = vectorFrom(bounds?.halfExtents)
  if (!center || !halfExtents) return null

  const minimum = center.clone().sub(halfExtents)
  const maximum = center.clone().add(halfExtents)
  const ray = direction.clone().normalize()
  let near = -Infinity
  let far = Infinity
  for (const axis of ['x', 'y', 'z']) {
    const start = Number(origin[axis])
    const delta = Number(ray[axis])
    if (Math.abs(delta) < 1e-8) {
      if (start < minimum[axis] || start > maximum[axis]) return null
      continue
    }
    const first = (minimum[axis] - start) / delta
    const second = (maximum[axis] - start) / delta
    near = Math.max(near, Math.min(first, second))
    far = Math.min(far, Math.max(first, second))
    if (near > far) return null
  }

  // If the camera starts inside the model bounds, use the forward exit point.
  const distance = near >= 0 ? near : far
  if (!Number.isFinite(distance) || distance < 0) return null
  return origin.clone().add(ray.mulScalar(distance))
}

const firstCameraDataModelIntersection = (samples, bounds) => {
  if (!bounds) return null
  for (const sample of samples) {
    if (!sample.direction || sample.direction.length() <= 1e-6) continue
    const focus = rayBoxIntersection(sample.position, sample.direction, bounds)
    if (focus) return focus
  }
  return null
}

const CAMERA_MOVEMENT_MIN_RADIUS_SCALE = 0.75
const CAMERA_MOVEMENT_MIN_RADIUS_FLOOR = 0.03

const normalizeAngleRadians = angle => {
  let normalized = Number(angle) || 0
  while (normalized <= -Math.PI) normalized += Math.PI * 2
  while (normalized > Math.PI) normalized -= Math.PI * 2
  return normalized
}

const shortestAngleInterval = (angles, padding = 0) => {
  if (!angles.length) return { min: -padding, max: padding, center: 0 }
  if (angles.length === 1) {
    const center = normalizeAngleRadians(angles[0])
    return { min: center - padding, max: center + padding, center }
  }

  const sorted = angles.map(normalizeAngleRadians).sort((left, right) => left - right)
  let largestGap = -Infinity
  let largestGapIndex = 0
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index]
    const next = index === sorted.length - 1 ? sorted[0] + Math.PI * 2 : sorted[index + 1]
    const gap = next - current
    if (gap > largestGap) {
      largestGap = gap
      largestGapIndex = index
    }
  }

  const start = sorted[(largestGapIndex + 1) % sorted.length]
  const end = sorted[largestGapIndex] < start
    ? sorted[largestGapIndex] + Math.PI * 2
    : sorted[largestGapIndex]
  const min = start - padding
  const max = end + padding
  return { min, max, center: (min + max) * 0.5 }
}

const expandAngleInterval = (interval, minSpan) => {
  const center = Number(interval?.center)
  const min = Number(interval?.min)
  const max = Number(interval?.max)
  if (![center, min, max, minSpan].every(Number.isFinite) || minSpan <= 0) return interval
  if (max - min >= minSpan) return interval
  const halfSpan = minSpan * 0.5
  return { min: center - halfSpan, max: center + halfSpan, center }
}

const horizontalYawCoverage = (positions, center) => {
  const angles = positions
    .map(position => {
      const dx = position.x - center.x
      const dz = position.z - center.z
      return Math.hypot(dx, dz) > 1e-6 ? Math.atan2(dz, dx) : null
    })
    .filter(Number.isFinite)
  if (!angles.length) return { angles, span: 0, full: false }
  const interval = shortestAngleInterval(angles)
  const span = interval.max - interval.min
  return {
    angles,
    span,
    full: angles.length >= 4 && span >= pc.math.DEG_TO_RAD * 250
  }
}

export function solveCameraDataFocusCenter(positions, directions, fallbackDistance = 1) {
  if (!positions.length) return null
  const rows = [[0, 0, 0], [0, 0, 0], [0, 0, 0]]
  const rhs = [0, 0, 0]

  positions.forEach((position, index) => {
    const direction = directions[index]
    if (!direction || direction.length() < 1e-6) return
    const d = direction.clone().normalize()
    const projector = [
      [1 - d.x * d.x, -d.x * d.y, -d.x * d.z],
      [-d.y * d.x, 1 - d.y * d.y, -d.y * d.z],
      [-d.z * d.x, -d.z * d.y, 1 - d.z * d.z]
    ]
    for (let row = 0; row < 3; row += 1) {
      rows[row][0] += projector[row][0]
      rows[row][1] += projector[row][1]
      rows[row][2] += projector[row][2]
      rhs[row] += projector[row][0] * position.x
        + projector[row][1] * position.y
        + projector[row][2] * position.z
    }
  })

  const determinant =
    rows[0][0] * (rows[1][1] * rows[2][2] - rows[1][2] * rows[2][1])
    - rows[0][1] * (rows[1][0] * rows[2][2] - rows[1][2] * rows[2][0])
    + rows[0][2] * (rows[1][0] * rows[2][1] - rows[1][1] * rows[2][0])

  if (Math.abs(determinant) > 1e-6) {
    const detX =
      rhs[0] * (rows[1][1] * rows[2][2] - rows[1][2] * rows[2][1])
      - rows[0][1] * (rhs[1] * rows[2][2] - rows[1][2] * rhs[2])
      + rows[0][2] * (rhs[1] * rows[2][1] - rows[1][1] * rhs[2])
    const detY =
      rows[0][0] * (rhs[1] * rows[2][2] - rows[1][2] * rhs[2])
      - rhs[0] * (rows[1][0] * rows[2][2] - rows[1][2] * rows[2][0])
      + rows[0][2] * (rows[1][0] * rhs[2] - rhs[1] * rows[2][0])
    const detZ =
      rows[0][0] * (rows[1][1] * rhs[2] - rhs[1] * rows[2][1])
      - rows[0][1] * (rows[1][0] * rhs[2] - rhs[1] * rows[2][0])
      + rhs[0] * (rows[1][0] * rows[2][1] - rows[1][1] * rows[2][0])
    const center = new pc.Vec3(detX / determinant, detY / determinant, detZ / determinant)
    if ([center.x, center.y, center.z].every(Number.isFinite)) return center
  }

  const averagePosition = positions
    .reduce((sum, position) => sum.add(position), new pc.Vec3())
    .mulScalar(1 / positions.length)
  const averageDirection = directions
    .filter(Boolean)
    .reduce((sum, direction) => sum.add(direction), new pc.Vec3())
  return averageDirection.length() > 1e-6
    ? averagePosition.add(averageDirection.normalize().mulScalar(fallbackDistance))
    : averagePosition
}

/**
 * Derive the same camera movement window used by 3dgs_web from every
 * cameraData frame. The result limits orbit/fly position to captured yaw,
 * pitch and radius ranges instead of using cameraData only as a start pose.
 */
export function readCameraDataMovementBounds(value, options = {}) {
  const samples = cameraDataItems(value)
    .map(item => ({ position: itemPosition(item), direction: itemLookDirection(item) }))
    .filter(sample => sample.position)
  const positions = samples.map(sample => sample.position)
  const directions = samples.map(sample => sample.direction)
  if (!positions.length) return null

  const min = positions[0].clone()
  const max = positions[0].clone()
  for (const position of positions.slice(1)) {
    min.x = Math.min(min.x, position.x)
    min.y = Math.min(min.y, position.y)
    min.z = Math.min(min.z, position.z)
    max.x = Math.max(max.x, position.x)
    max.y = Math.max(max.y, position.y)
    max.z = Math.max(max.z, position.z)
  }

  const diagonal = Math.max(max.clone().sub(min).length(), 1)
  const centerOverride = vectorFrom(options.center)
  const center = centerOverride || solveCameraDataFocusCenter(positions, directions, diagonal)
  if (!center) return null

  const radialDirections = positions
    .map(position => position.clone().sub(center))
    .filter(offset => offset.length() > 1e-6)
    .map(offset => offset.normalize())
  const distances = positions
    .map(position => position.distance(center))
    .filter(distance => Number.isFinite(distance) && distance > 1e-6)
  if (!radialDirections.length || !distances.length) return null

  const worldUp = new pc.Vec3(0, 1, 0)
  const horizontalDirections = radialDirections
    .map(direction => direction.clone().sub(worldUp.clone().mulScalar(direction.dot(worldUp))))
    .filter(direction => direction.length() > 1e-6)
    .map(direction => direction.normalize())
  let forward = horizontalDirections.reduce((sum, direction) => sum.add(direction), new pc.Vec3())
  if (forward.length() < 1e-6) forward = horizontalDirections[0] || new pc.Vec3(0, 0, 1)
  forward.normalize()
  let right = new pc.Vec3().cross(worldUp, forward)
  if (right.length() < 1e-6) right = new pc.Vec3().cross(new pc.Vec3(1, 0, 0), forward)
  right.normalize()
  const up = worldUp.clone()

  const yaws = []
  const pitches = []
  for (const direction of radialDirections) {
    yaws.push(Math.atan2(direction.dot(right), direction.dot(forward)))
    pitches.push(Math.atan2(direction.y, Math.hypot(direction.x, direction.z)))
  }

  const minDistance = Math.min(...distances)
  const maxDistance = Math.max(...distances)
  const radiusPadding = Math.max(maxDistance * 0.008, diagonal * 0.006, 0.01)
  const minRadius = Math.max(
    CAMERA_MOVEMENT_MIN_RADIUS_FLOOR,
    (minDistance - radiusPadding) * CAMERA_MOVEMENT_MIN_RADIUS_SCALE
  )
  const maxRadius = Math.max(
    maxDistance + radiusPadding,
    minRadius + Math.max(radiusPadding, 0.1)
  )

  const yawPadding = pc.math.DEG_TO_RAD * 7
  const pitchPadding = pc.math.DEG_TO_RAD * 3
  const minYawSpan = pc.math.DEG_TO_RAD * (positions.length <= 2 ? 34 : 24)
  const minPitchSpan = pc.math.DEG_TO_RAD * (positions.length <= 2 ? 12 : 8)
  const yawInterval = shortestAngleInterval(yaws)
  const yawCoverage = horizontalYawCoverage(positions, center)
  const centeredForward = right.clone().mulScalar(Math.sin(yawInterval.center))
    .add(forward.clone().mulScalar(Math.cos(yawInterval.center)))
  if (centeredForward.length() > 1e-6) {
    forward.copy(centeredForward.normalize())
    right = new pc.Vec3().cross(up, forward)
    if (right.length() < 1e-6) right = new pc.Vec3().cross(worldUp, forward)
    right.normalize()
  }

  const centeredYaws = radialDirections.map(direction => (
    Math.atan2(direction.dot(right), direction.dot(forward))
  ))
  const centeredYawInterval = expandAngleInterval(
    shortestAngleInterval(centeredYaws, yawPadding),
    minYawSpan
  )
  const minYaw = yawCoverage.full ? -Math.PI : centeredYawInterval.min
  const maxYaw = yawCoverage.full ? Math.PI : centeredYawInterval.max

  const pitchCenter = (Math.min(...pitches) + Math.max(...pitches)) * 0.5
  const rawMinPitch = Math.max(-pc.math.DEG_TO_RAD * 89, Math.min(...pitches) - pitchPadding)
  const rawMaxPitch = Math.min(pc.math.DEG_TO_RAD * 89, Math.max(...pitches) + pitchPadding)
  const pitchInterval = expandAngleInterval(
    { min: rawMinPitch, max: rawMaxPitch, center: pitchCenter },
    minPitchSpan
  )
  const minPitch = Math.max(-pc.math.DEG_TO_RAD * 89, pitchInterval.min)
  const maxPitch = Math.min(pc.math.DEG_TO_RAD * 89, pitchInterval.max)
  const orbitPitchRange = new pc.Vec2(
    Math.max(-89, -maxPitch / pc.math.DEG_TO_RAD),
    Math.min(89, -minPitch / pc.math.DEG_TO_RAD)
  )

  return {
    mode: 'sphere-window',
    center,
    forward,
    right,
    up,
    minRadius,
    maxRadius,
    minYaw,
    maxYaw,
    minPitch,
    maxPitch,
    orbitPitchRange,
    softMargin: Math.max(diagonal * 0.12, maxDistance * 0.08, 0.8),
    pullStrength: 12,
    sampleCount: positions.length,
    fullYawCoverage: yawCoverage.full
  }
}

export function createAabbMovementBounds(bounds) {
  const center = vectorFrom(bounds?.center)
  const halfExtents = vectorFrom(bounds?.halfExtents)
  if (!center || !halfExtents) return null
  const radius = Math.max(halfExtents.x, halfExtents.y, halfExtents.z, 0.1)
  const diagonal = Math.max(halfExtents.length() * 2, radius * 2, 1)
  const minRadius = Math.max(
    radius * 0.08 * CAMERA_MOVEMENT_MIN_RADIUS_SCALE,
    CAMERA_MOVEMENT_MIN_RADIUS_FLOOR
  )
  return {
    mode: 'sphere-window',
    center,
    forward: new pc.Vec3(0, 0, 1),
    right: new pc.Vec3(1, 0, 0),
    up: new pc.Vec3(0, 1, 0),
    minRadius,
    maxRadius: Math.max(diagonal * 4, radius * 6, minRadius + 1),
    minYaw: -Math.PI,
    maxYaw: Math.PI,
    minPitch: -pc.math.DEG_TO_RAD * 89,
    maxPitch: pc.math.DEG_TO_RAD * 89,
    orbitPitchRange: new pc.Vec2(-89, 89),
    softMargin: Math.max(radius * 0.5, 0.8),
    pullStrength: 8,
    sampleCount: 0,
    fullYawCoverage: true
  }
}

export function readCameraDataPose(value, options = {}) {
  const items = cameraDataItems(value)
  const samples = items
    .map(item => ({
      item,
      position: itemPosition(item),
      direction: itemLookDirection(item)
    }))
    .filter(sample => sample.position)
  if (!samples.length) return null

  const first = samples[0]
  const explicitFocus = itemExplicitFocus(first.item)
  if (explicitFocus && focusIsInFrontOfSamples(explicitFocus, [first])) {
    return { position: first.position, focus: explicitFocus, focusSource: 'explicit' }
  }

  const positions = samples.map(sample => sample.position)
  const directions = samples.map(sample => sample.direction)
  const solvedFocus = solveCameraDataFocusCenter(positions, directions, cameraPathDiagonal(positions))
  if (solvedFocus && focusIsInFrontOfSamples(solvedFocus, samples)) {
    return { position: first.position, focus: solvedFocus, focusSource: 'camera_intersection' }
  }

  // A mathematical solution behind one or more cameras is not a valid view.
  // Fall back in cameraData order to the first frame whose forward ray reaches
  // the model bounds, then the second frame, and so on.
  const modelFocus = firstCameraDataModelIntersection(samples, options.bounds)
  if (modelFocus) return { position: first.position, focus: modelFocus, focusSource: 'model_bounds' }

  const fallbackDirection = directions.find(direction => direction && direction.length() > 1e-6)
    || new pc.Vec3(0, 0, 1)
  const fallbackFocus = positions[0].clone().add(
    fallbackDirection.clone().normalize().mulScalar(cameraPathDiagonal(positions))
  )
  return focusIsInFrontOfSamples(fallbackFocus, [first])
    ? { position: first.position, focus: fallbackFocus, focusSource: 'fallback' }
    : null
}

export function readCameraDataFrames(value) {
  return cameraDataItems(value)
    .map(item => ({
      position: itemPosition(item),
      direction: itemLookDirection(item)
    }))
    .filter(frame => frame.position && frame.direction && frame.direction.length() > 1e-6)
}

/**
 * Convert captured camera frames into playable camera poses. Camera data from
 * older tasks may only include a position and rotation, so derive a stable
 * look target from the captured directions when an explicit focus is absent.
 */
export function readCameraDataTrajectory(value, options = {}) {
  const items = cameraDataItems(value)
  if (items.length < 2) return []

  const samples = items
    .map(item => ({
      item,
      position: itemPosition(item),
      direction: itemLookDirection(item),
      explicitFocus: itemExplicitFocus(item)
    }))
    .filter(sample => sample.position)
  if (samples.length < 2) return []

  const positions = samples.map(sample => sample.position)
  const directions = samples.map(sample => sample.direction)
  const pathDiagonal = cameraPathDiagonal(positions)
  const solvedFocus = solveCameraDataFocusCenter(positions, directions, pathDiagonal)
  const fallbackFocus = solvedFocus || positions[0].clone().add(new pc.Vec3(0, 0, pathDiagonal))
  const minTargetDistance = Math.max(pathDiagonal * 0.2, 0.1)

  return samples.map(sample => {
    let focus = sample.explicitFocus?.clone() || null
    if (!focus && sample.direction) {
      const targetDistance = Math.max(sample.position.distance(fallbackFocus), minTargetDistance)
      focus = sample.position.clone().add(sample.direction.clone().normalize().mulScalar(targetDistance))
    }
    if (!focus) focus = fallbackFocus.clone()
    return {
      position: sample.position.clone(),
      focus,
      duration: Math.max(0.04, Number(options.frameDuration) || 0.22)
    }
  })
}
